import { FC } from 'react';

import { Badge, Link, Tooltip } from '@dbt-labs/sourdough';

import { SearchFieldType } from '@/__generated__/graphql';
import { toTitleCase } from '@/util/string';
import {
  BOLD_TAG_REGEX,
  SanitizeBoldText,
} from '../../project/search/components/BoldedText';

export type HighlightPillsProps = {
  highlights: Partial<Record<SearchFieldType, string[]>>;
  query: string;
  getColumnLink?: (columnName: string) => string;
};

const getFieldLabel = (field: SearchFieldType): string => {
  switch (field) {
    case SearchFieldType.ColumnDescription:
      return 'Column desc';
    case SearchFieldType.Column:
      return 'Column';
    case SearchFieldType.Code:
      return 'Code';
    case SearchFieldType.Description:
      return 'Description';
    case SearchFieldType.Name:
      return 'Name';
    case SearchFieldType.Relation:
      return 'Relation';
    default:
      return toTitleCase(field);
  }
};

const truncateHighlight = (field: SearchFieldType, text: string): string => {
  const MAX_LENGTH = 200;
  switch (field) {
    case SearchFieldType.Code:
    case SearchFieldType.Description:
    case SearchFieldType.ColumnDescription:
      if (text.length > MAX_LENGTH) {
        return `${text.slice(0, MAX_LENGTH)}...`;
      }
      return text;
    default:
      return text;
  }
};

const formatHighlightsForTooltip = (
  field: SearchFieldType,
  highlightTexts: string[],
): string => {
  // Join multiple highlights with line breaks for display
  const joined = highlightTexts.join('\n');
  return truncateHighlight(field, joined);
};

const ColumnLinksTooltip: FC<{
  highlightTexts: string[];
  query: string;
  getColumnLink: (columnName: string) => string;
}> = ({ highlightTexts, query, getColumnLink }) => {
  return (
    <div className="max-w-md whitespace-pre-wrap">
      {highlightTexts.map((text, index) => {
        const columnName = text.replace(BOLD_TAG_REGEX, '');
        return (
          <div key={index}>
            <Link to={getColumnLink(columnName)} isInternal>
              <SanitizeBoldText text={text} query={query} />
            </Link>
          </div>
        );
      })}
    </div>
  );
};

export const HighlightPills: FC<HighlightPillsProps> = ({
  highlights,
  query,
  getColumnLink,
}) => {
  // Filter out "name" field - name is highlighted inline in the result item
  const entries = (Object.entries(highlights) as [SearchFieldType, string[]][]).filter(
    ([field]) => field !== SearchFieldType.Name,
  );

  if (entries.length === 0) {
    return null;
  }

  const getBadgeText = (field: SearchFieldType, highlightTexts: string[]): string => {
    const baseLabel = getFieldLabel(field);
    const count = highlightTexts.length;
    const displayCount = count >= 11 ? '10+' : count.toString();
    return `${baseLabel} (${displayCount})`;
  };

  return (
    <div className="flex flex-wrap items-center gap-2 pt-2">
      <span className="text-xs text-fgDecorative">Matches:</span>
      {entries.map(([field, highlightTexts]) => (
        <Tooltip
          key={field}
          content={
            field === SearchFieldType.Column && getColumnLink ? (
              <ColumnLinksTooltip
                highlightTexts={highlightTexts}
                query={query}
                getColumnLink={getColumnLink}
              />
            ) : (
              <div className="max-w-md whitespace-pre-wrap">
                <SanitizeBoldText
                  text={formatHighlightsForTooltip(field, highlightTexts)}
                  query={query}
                />
              </div>
            )
          }
          placement="bottom-start"
        >
          <Badge text={getBadgeText(field, highlightTexts)} type="default" size="xs" />
        </Tooltip>
      ))}
    </div>
  );
};
